import { TFunction } from 'next-i18next';
import prepareFriendlyUri from '@utils/prepareFriendlyUri';
import {
  ILocation,
  IGoogleMapLocation,
  IPointOfPresence,
  IPointOfPresenceGalleryItem,
  IPointOfPresenceWithDetails,
  IPointOfPresenceWithDistance,
  IPointOfDeliveryCheckout,
  IPointOfDelivery,
  IPointOfSaleCheckout,
  IUserLocation,
} from '@features/checkout/typescript';
/**
 * window.getPublicEnv czyta tylko window.properties (getPublicEnv.js); brak klucza → ''.
 * Next nadal wstrzykuje NEXT_PUBLIC_* w process.env — bez fallbacku SSR i klient się rozjeżdżają.
 */
const getNavigeBaseUrl = (): string => {
  const fromEnv = (process.env.NEXT_PUBLIC_NAVIGE_URL as string | undefined) || '';
  if (typeof window !== 'undefined' && typeof window.getPublicEnv !== 'undefined') {
    return window.getPublicEnv('NEXT_PUBLIC_NAVIGE_URL') || fromEnv;
  }
  return fromEnv;
};

export const getShowroomType = (code: string | undefined, t: TFunction): string => {
  if (code) {
    if (code === 'VOX_INTERIORS') {
      return t('shoowroomMapFilters.interiors');
    }
    if (code === 'VOXBOX_STUDIO') {
      return t('shoowroomMapFilters.voxboxTitle');
    }
    if (code === 'VOX_FURNITURES') {
      return t('shoowroomMapFilters.furnitureTitle');
    }
    if (code === 'VOX_DIP') {
      return t('shoowroomMapFilters.dipTitle');
    }
  }
  return t('shoowroomMapFilters.defaultShowroomType');
};

export const getShowroomName = (showroom: IPointOfPresence | IPointOfPresenceWithDetails, t: TFunction): string =>
  showroom?.group?.listingName ? showroom.group.listingName : getShowroomType(showroom?.group?.code, t);

export const getPointName = (point: IPointOfSaleCheckout | IPointOfDeliveryCheckout, t: TFunction): string =>
  point?.groupData?.listingName ? point.groupData.listingName : getShowroomType(point?.groupData?.code, t);

export const getShowroomReviewBoosterURL = (
  showroom: IPointOfPresence | IPointOfPresenceWithDetails,
): string | undefined =>
  showroom?.ids &&
  'REVIEW-BOOSTER-URL' in showroom.ids &&
  typeof showroom.ids['REVIEW-BOOSTER-URL'] === 'string' &&
  showroom.ids['REVIEW-BOOSTER-URL'].includes('https://reviews.center.ai/')
    ? showroom.ids['REVIEW-BOOSTER-URL']
    : undefined;

export const getPimInternalId = (showroom: IPointOfPresence): string | undefined =>
  showroom?.ids && 'INTERNAL-PIM-ID' in showroom.ids && typeof showroom.ids['INTERNAL-PIM-ID'] === 'string'
    ? showroom.ids['INTERNAL-PIM-ID']
    : undefined;

export const prepareShowroomUrl = (
  code: string | undefined,
  name: string | undefined,
  pimInternalId: string | undefined,
  t: TFunction,
): string => {
  if (name && pimInternalId) {
    const type = getShowroomType(code, t);
    const friendlyUriShowroomtName = prepareFriendlyUri(`${type}-${name || 'vox'}`);
    return `/showrooms/${friendlyUriShowroomtName}/${pimInternalId}`;
  }
  return '';
};

export const navigationPossible = (location: ILocation | undefined) =>
  !!(location && location?.latitude && location?.longitude && getNavigeBaseUrl());

export const getClearLocation = (): IGoogleMapLocation => ({
  lat: null,
  lng: null,
});

export const getClearUserLocation = (): IUserLocation => ({
  location: getClearLocation(),
  country: null,
  city: null,
  postcode: null,
});

export const getUserGeolocation = (getFromBrowser = true): Promise<IGoogleMapLocation> =>
  new Promise((res) => {
    const userLocationResult = localStorage?.getItem('userLocation');
    if (userLocationResult) {
      const userLocation = JSON.parse(userLocationResult);
      if (userLocation?.lat && userLocation?.lng) {
        res(userLocation);
      }
    }
    const timeOut = setTimeout(
      () => {
        res({
          lat: null,
          lng: null,
        });
      },
      getFromBrowser ? 10000 : 0,
    );
    if (getFromBrowser) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((p) => {
          clearTimeout(timeOut);
          const newUserLocation = {
            lat: p.coords.latitude,
            lng: p.coords.longitude,
          };
          localStorage.setItem('userLocation', JSON.stringify(newUserLocation));
          res(newUserLocation);
        });
      }
    }
  });

/** Builds maps navigation URL when both showroom and user location are available. Returns undefined otherwise. */
export const getNavigateUrl = (
  showroomLocation: ILocation | undefined,
  userLocation: IGoogleMapLocation | null,
): string | undefined => {
  if (!showroomLocation || !navigationPossible(showroomLocation) || !userLocation?.lat || !userLocation?.lng) {
    return undefined;
  }
  const base = getNavigeBaseUrl();
  return `${base}${userLocation.lat},${userLocation.lng}/${showroomLocation.latitude},${showroomLocation.longitude}/@${showroomLocation.latitude},${showroomLocation.longitude}`;
};

export const onClickNavigate = async (
  showroomlLocation: ILocation | undefined,
  userLocation: IGoogleMapLocation | null = null,
) => {
  if (showroomlLocation && navigationPossible(showroomlLocation)) {
    let url = getNavigeBaseUrl();
    if (userLocation && userLocation?.lat && userLocation?.lng) {
      url += `${userLocation.lat},${userLocation.lng}`;
    } else {
      const userGeolocation = await getUserGeolocation();
      if (userGeolocation && userGeolocation?.lat && userGeolocation?.lng) {
        url += `${userGeolocation.lat},${userGeolocation.lng}`;
      }
    }
    url += `/${showroomlLocation.latitude},${showroomlLocation.longitude}/@${showroomlLocation.latitude},${showroomlLocation.longitude}`;
    window.open(url, '_blank');
  }
};

export const handleEmptyGallery = (gallery: Array<IPointOfPresenceGalleryItem>): Array<IPointOfPresenceGalleryItem> => {
  if (!gallery || gallery.length === 0) {
    return [
      {
        sizes: {
          ORGINAL: '/img/no-photo-showroom.png',
        },
        type: 'image',
      },
    ];
  }
  return gallery;
};

export const findPlaceFromQuery = (
  _map: google.maps.Map | HTMLDivElement,
  newSearchWord: string,
): Promise<IGoogleMapLocation | null> =>
  new Promise((res) => {
    const request = {
      query: newSearchWord,
      fields: ['name', 'formatted_address', 'geometry'],
    };
    const service = new google.maps.places.PlacesService(_map);
    service.findPlaceFromQuery(request, (results, status) => {
      if (
        status === google.maps.places.PlacesServiceStatus.OK &&
        results &&
        results[0]?.formatted_address &&
        results[0].formatted_address.toUpperCase().indexOf(newSearchWord.toUpperCase()) !== -1 &&
        results[0]?.geometry?.location
      ) {
        res({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });
      } else {
        res(null);
      }
    });
  });
export const sortShoowroomsWithDistance = (
  showroomsWithDistance: IPointOfPresenceWithDistance[],
): IPointOfPresenceWithDistance[] =>
  showroomsWithDistance.sort((a, b) => {
    if (a.distance < b.distance) return -1;
    if (a.distance > b.distance) return 1;
    return 0;
  });

export const getDistanceFromLatLonInKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const deg2rad = (deg) => deg * (Math.PI / 180);
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
};

export const foundShowroomsByLocation = (
  location: IGoogleMapLocation,
  showrooms: IPointOfPresence[] | IPointOfDeliveryCheckout[] | IPointOfSaleCheckout[],
  maxDistance = 100,
) => {
  if (!Array.isArray(showrooms)) {
    return {
      showrooms: [],
      newDisabledShowrooms: [],
    };
  }

  if (location.lat && location.lng) {
    const { lat, lng } = location;
    const showroomsWithDistance: IPointOfPresenceWithDistance[] = [];
    const newDisabledShowrooms: IPointOfPresence[] | IPointOfDeliveryCheckout[] | IPointOfSaleCheckout[] = [];

    Array.isArray(showrooms) &&
      showrooms.forEach((showroom) => {
        let latitude: number | null = null;
        let longitude: number | null = null;
        if (showroom.location?.latitude && showroom.location?.longitude) {
          latitude = showroom.location.latitude;
          longitude = showroom.location.longitude;
        } else if (showroom?.geoLatitude && showroom?.geoLongitude) {
          latitude = showroom.geoLatitude;
          longitude = showroom.geoLongitude;
        }
        if (latitude && longitude) {
          const distance = getDistanceFromLatLonInKm(latitude, longitude, lat, lng);
          if (distance < maxDistance) {
            showroomsWithDistance.push({
              distance,
              point: showroom,
            });
          } else {
            newDisabledShowrooms.push(showroom);
          }
        }
      });
    const sortShowroomsWithDistance = sortShoowroomsWithDistance(showroomsWithDistance);
    return {
      showrooms: sortShowroomsWithDistance.map((item) => item.point),
      newDisabledShowrooms,
    };
  }
  return {
    showrooms: [],
    newDisabledShowrooms: [],
  };
};

export const sortShowroomsByCity = (showrooms: IPointOfPresence[]): IPointOfPresence[] =>
  showrooms.sort((a, b) =>
    a?.address?.city && b?.address?.city ? a.address.city.localeCompare(b?.address?.city || '') : 0,
  );

export const filterByMerchandisingDisplayStatus = (
  showrooms: IPointOfPresence[],
  merchandisingDisplayStatus: string,
): IPointOfPresence[] =>
  showrooms.filter((showroom) => showroom.merchandisingDisplayStatus === merchandisingDisplayStatus);

export const sortShowroomsByCountry = (showrooms: IPointOfPresence[]): IPointOfPresence[] =>
  showrooms.sort((a, b) =>
    a?.country?.name && b?.country?.name ? a.country.name.localeCompare(b?.country?.name || '') : 0,
  );

export const sortShowroomsByCode = (showrooms: IPointOfPresence[]): IPointOfPresence[] =>
  showrooms.sort((a, b) => (a?.group?.code && b?.group?.code ? a.group.code.localeCompare(b?.group?.code || '') : 0));

const merchandisingDisplayStatusTemplate = ['STATUS_AVAILABLE', 'STATUS_LIMITED_AVAILABILITY', 'STATUS_UNAVAILABILITY'];

const getMerchandisingDisplayStatusOrder = (status: string | null | undefined): number => {
  const index = merchandisingDisplayStatusTemplate.indexOf(status || 'STATUS_UNAVAILABILITY');
  return index === -1 ? merchandisingDisplayStatusTemplate.length : index;
};

export const sortShowroomsByMerchandisingDisplayStatus = (showrooms: IPointOfPresence[]): IPointOfPresence[] =>
  showrooms.sort(
    (a, b) =>
      getMerchandisingDisplayStatusOrder(a?.merchandisingDisplayStatus) -
      getMerchandisingDisplayStatusOrder(b?.merchandisingDisplayStatus),
  );

export const filterShowroomsByCode = (showrooms: IPointOfPresence[], codes: string[]): IPointOfPresence[] =>
  showrooms.filter((showroom) => codes.includes(showroom?.group?.code || 'VOX_INTERIORS'));

export const getOpeningHours = (
  showroom: IPointOfPresence | IPointOfDeliveryCheckout | IPointOfDelivery,
  _t: TFunction,
  shortDayName = false,
) => {
  let openingHours = showroom?.openingHours?.description || '';
  if (showroom?.openingHours?.days?.length) {
    let opens = '';
    let closes = '';
    let firstDayOfWeek = '';
    let lastDayOfWeek = '';
    const openingHoursGroups: Record<string, string>[] = [];

    showroom.openingHours.days.forEach((item) => {
      if (openingHoursGroups.length === 0 && !firstDayOfWeek) {
        opens = item.opens;
        closes = item.closes;
        firstDayOfWeek = item.dayOfWeek;
      } else if (opens === item.opens && closes === item.closes) {
        lastDayOfWeek = item.dayOfWeek;
      } else {
        openingHoursGroups.push({
          opens,
          closes,
          firstDayOfWeek,
          lastDayOfWeek,
        });
        opens = item.opens;
        closes = item.closes;
        firstDayOfWeek = item.dayOfWeek;
        lastDayOfWeek = '';
      }
    });

    if (opens && closes && firstDayOfWeek) {
      openingHoursGroups.push({ opens, closes, firstDayOfWeek, lastDayOfWeek });
    }

    if (openingHoursGroups.length) {
      openingHours = '';
      openingHoursGroups.forEach((item) => {
        if (item.opens === '00:00' && item.closes === '00:00') return;
        if (openingHours.length) openingHours += '\n';
        openingHours += _t(shortDayName ? `shorDayOfWeek.${item.firstDayOfWeek}` : `dayOfWeek.${item.firstDayOfWeek}`);
        if (item.lastDayOfWeek) {
          openingHours += ' - ';
          openingHours += _t(shortDayName ? `shorDayOfWeek.${item.lastDayOfWeek}` : `dayOfWeek.${item.lastDayOfWeek}`);
        }
        openingHours += ': ';
        openingHours += `${item.opens} - ${item.closes}`;
      });
    }
  }
  return openingHours;
};

const getCity = (showroom): string => {
  if (showroom?.city) return showroom.city;
  return showroom?.address?.city ? showroom?.address?.city : '';
};

const getLocation = (showroom): IGoogleMapLocation | undefined => {
  if (showroom?.geoLatitude && showroom?.geoLatitude)
    return {
      lat: showroom.geoLatitude,
      lng: showroom.geoLongitude,
    };
  return showroom?.location?.latitude && showroom?.location?.longitude
    ? {
        lat: showroom?.location?.latitude,
        lng: showroom?.location?.longitude,
      }
    : undefined;
};

export const findShowroomLocationByCity = (
  points: IPointOfPresence[] | IPointOfDeliveryCheckout[] | IPointOfSaleCheckout[],
  searchCity: string,
): IGoogleMapLocation | undefined => {
  const normalizedSearchCity = searchCity.trim().toLowerCase();
  let result = (points as Array<IPointOfPresence | IPointOfDeliveryCheckout | IPointOfSaleCheckout>).find(
    (point: IPointOfPresence | IPointOfDeliveryCheckout | IPointOfSaleCheckout) =>
      getCity(point).trim().toLowerCase() === normalizedSearchCity,
  );
  if (!result)
    result = (points as Array<IPointOfPresence | IPointOfDeliveryCheckout | IPointOfSaleCheckout>).find(
      (point: IPointOfPresence | IPointOfDeliveryCheckout | IPointOfSaleCheckout) =>
        getCity(point).trim().toLowerCase().includes(normalizedSearchCity),
    );
  return result ? getLocation(result) : undefined;
};

export const findAddress = (newUserLocation: IUserLocation, type: string, longName: string): IUserLocation => {
  if (type && longName) {
    if (!newUserLocation.city && type === 'locality') {
      newUserLocation.city = longName;
    } else if (!newUserLocation.country && type === 'country') {
      newUserLocation.country = longName;
    }
  }
  return newUserLocation;
};

export const getUserAddress = async (_newUserLocation: IUserLocation): Promise<IUserLocation> => {
  let newUserLocation = _newUserLocation;
  if (newUserLocation.location.lat && newUserLocation.location.lng) {
    const location = new google.maps.LatLng(+newUserLocation.location.lat, +newUserLocation.location.lng);
    await new google.maps.Geocoder().geocode({ location }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results && results[0]) {
          for (let i = 0; i < results[0].address_components.length; i += 1) {
            for (let b = 0; b < results[0].address_components[i].types.length; b += 1) {
              newUserLocation = findAddress(
                newUserLocation,
                results[0].address_components[i].types[b],
                results[0].address_components[i].long_name,
              );
              if (newUserLocation.city && newUserLocation.country) break;
            }
          }
        }
      }
    });
  }
  return newUserLocation;
};
